import React, {useEffect, useId, useState} from 'react';
import {Button, Col, Form, Modal} from 'react-bootstrap';
import EmptyPage from '../../components/EmptyPage';
import {MemberWorkExtendedDTO} from '../../dtos/MemberWorkExtendedDTO';
import {NetworkCalls} from '../../NetworkCalls';
import DeleteDialog from '../../components/DeleteDialog';
import uuid from 'react-uuid';
import MemberWorkAttendeeDTO from '../../dtos/MemberWorkAttendeeDTO';
import {Link} from 'react-router-dom';
import SearchMember from '../../components/SearchMember';
import DateTime, {TimeConstraints} from 'react-datetime';
import {Moment} from 'moment/moment';
import {isMoment} from 'moment-timezone';
import {APILoadingStatus, APIProvider, Map, Marker, useApiLoadingStatus} from '@vis.gl/react-google-maps';
import GenericDialog from '../../components/GenericDialog';
import {useSelectedOrganisationContext} from "../../custom-hooks/useOrganisation";

export default function MemberWorkExtended() {
  function getInitialAppointment() {
    let date = new Date();
    return new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate() + 1,
      6,
      0,
      0,
      0
    );
  }

  const status = useApiLoadingStatus();

    const { selectedOrganisation } = useSelectedOrganisationContext();

  useEffect(() => {
    console.log(`Status: ${status}`);
    if (status === APILoadingStatus.FAILED) {
      console.log(':(');

      return;
    }
  }, [status]);

  const [memberWorkData, setMemberWorkData] = useState<MemberWorkExtendedDTO[]>(
    []
  );
  const [selectedMemberWork, setSelectedMemberWork] = useState<
    MemberWorkExtendedDTO | undefined
  >();
  const [show, setShow] = useState<boolean>(false);
  const [appointment, setAppointment] = useState<Date | null>(getInitialAppointment());
  const [maxDate] = useState<Date>(
    new Date(
      new Date().getFullYear() + 1,
      new Date().getMonth(),
      new Date().getDay()
    )
  );
  const [title, setTitle] = useState<string>();
  const [additionalInformation, setAdditionalInformation] = useState<string>();
  const [maxParticipants, setMaxParticipants] = useState<number>();
  const [lat, setLat] = useState<number | undefined>();
  const [lng, setLng] = useState<number | undefined>();
  const [tabIndex, setTabIndex] = useState<number>(0);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [showSendPushNotificationDialog, setShowSendPushNotificationDialog] = useState<boolean>(false);
  const [showDeleteAttendeeDialog, setShowDeleteAttendeeDialog] =
    useState<boolean>(false);
  const [memberWorkAttendees, setMemberWorkAttendees] = useState<
    MemberWorkAttendeeDTO[]
  >([]);

  const excludedNumbers = React.useMemo(
    () => new Set(memberWorkAttendees.map(a => String(a.memberNumber))),
    [memberWorkAttendees]
  );

  const [showByGone, setShowByGone] = useState<boolean>(false);
  const [selectedAttendee, setSelectedAttendee] = useState<
    MemberWorkAttendeeDTO | undefined
  >(undefined);
  const [showMemberSearch, setShowMemberSearch] = useState<boolean>(false);

  const timeConstraints = {minutes: {step: 15}} as TimeConstraints;

  const formatUTC = (d: Date | string | number) =>
    new Intl.DateTimeFormat('de-DE', {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: 'UTC',     // <— wichtig
      hour12: false
    }).format(typeof d === 'number' ? new Date(d) : new Date(d));

  const isActive = (a?: MemberWorkExtendedDTO, b?: MemberWorkExtendedDTO) =>
    a?.id && b?.id && a.id === b.id;

  const inactiveClasses = 'nav-link';
  const activeClasses = 'nav-link active';

  useEffect(() => {
    if (selectedMemberWork) {
      setTitle(selectedMemberWork.title ?? '');
      setLat(selectedMemberWork.latitude);
      setLng(selectedMemberWork.longitude);
      setAdditionalInformation(selectedMemberWork.additionalInformation ?? '');
      setMaxParticipants(selectedMemberWork.maxParticipants);
      setAppointment(selectedMemberWork.start ?? null);
    }
  }, [selectedMemberWork]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (tabIndex === 1) {
      loadMemberWorkAttendees();
    }
  }, [tabIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setAppointment(getInitialAppointment());

    const fetchData = async () => {
      setMemberWorkData(await NetworkCalls.getMemberWorkExtended(false));
    };
    fetchData().catch(console.error);
  }, []);

  const ModalDialog = () => {
    const modalId = useId();
    if (!selectedOrganisation) {
      return <></>;
    }

    return (
      <Modal show={show} onHide={handleClose} id={modalId}>
        <Modal.Header closeButton>
          <Modal.Title>Arbeitsdienst einstellen</Modal.Title>
        </Modal.Header>
        <Modal.Body>{detailsPageMemberWork('map')}</Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            disabled={
              !title ||
              !maxParticipants || maxParticipants < 1 ||
              !appointment ||
              lat === undefined || lng === undefined
            }
            onClick={() => {
              if (title && maxParticipants && appointment && lat !== undefined && lng !== undefined) {
                persist({
                  title,
                  maxParticipants,
                  start: getAppointmentWithSelectedTime(appointment),
                  latitude: lat,
                  longitude: lng,
                  additionalInformation,
                  organisationId: selectedOrganisation.id
                }).then(() => setShow(false));
              }
            }}
          >
            Speichern
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  // @ts-ignore
  const handleChange = (e) => {
    e.preventDefault();
    setAdditionalInformation(e.target.value);
  };

  const persist = async (dto: MemberWorkExtendedDTO) => {
    await NetworkCalls.addMemberWorkExtended(dto);
    setShowByGone(false);
    setMemberWorkData(await NetworkCalls.getMemberWorkExtended(false));
  };

  const update = async (dto: MemberWorkExtendedDTO) => {
    await NetworkCalls.updateMemberWorkExtended(dto);
    setShowByGone(false);
    setMemberWorkData(await NetworkCalls.getMemberWorkExtended(false));
  };

  const divStyle = {
    width: '100%',
    height: '300px'
  };

  const handleShow = () => {
    setShow(true);
    setSelectedMemberWork(undefined);
    setTitle('');
    setAdditionalInformation('');
    setMaxParticipants(undefined);
    setLat(undefined);
    setLng(undefined);
    setAppointment(null);
  };

  const handleClose = () => setShow(false);

  function createRows() {
    return memberWorkAttendees.map((elem) => {
      return (
        <tr key={uuid()} className="user-select-auto">
          <td>{elem.memberNumber}</td>
          <td>{elem.firstName}</td>
          <td>{elem.lastName}</td>
          <td>
            <Form.Check // prettier-ignore
              type={'checkbox'}
              id={`default-checkbox`}
              checked={elem.attended}
              onChange={async (checkbox) => {
                const memberWorkAttendance = {
                  memberNumber: elem.memberNumber,
                  memberWorkId: elem.memberWork.id!,
                  organisationId: elem.memberWork.organisationId!
                };
                if (checkbox.target.checked) {
                  await NetworkCalls.addAttendanceToMemberWork(
                    memberWorkAttendance
                  );
                } else {
                  await NetworkCalls.removeAttendanceToMemberWork(
                    memberWorkAttendance
                  );
                }
                loadMemberWorkAttendees();
              }}
            />
          </td>
          <td>
            <Button
              variant="danger"
              onClick={() => {
                setShowDeleteAttendeeDialog(true);
                setSelectedAttendee(elem);
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="icon icon-tabler icon-tabler-trash"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                strokeWidth="2"
                stroke="currentColor"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                <path d="M4 7l16 0"></path>
                <path d="M10 11l0 6"></path>
                <path d="M14 11l0 6"></path>
                <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12"></path>
                <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3"></path>
              </svg>
            </Button>
          </td>
        </tr>
      );
    });
  }

  function showParticipants() {
    return (
      <table className="table table-hover">
        <thead>
        <tr key={uuid()}>
          <th scope="col">Mitgliedsnummer</th>
          <th scope="col">Vorname</th>
          <th scope="col">Nachname</th>
          <th scope="col">Teilgenommen</th>
          <th scope="col">Entfernen</th>
        </tr>
        </thead>
        <tbody id="table-content">{createRows()}</tbody>
      </table>
    );
  }

  const loadMemberWorkAttendees = () => {
    if (selectedMemberWork) {
      NetworkCalls.getMemberWorkAttendees(selectedMemberWork).then((value) =>
        setMemberWorkAttendees(value)
      );
    }
  };

  const showAllDetails = () => {
    return (
      <div className="row">
        <DeleteDialog
          onDeleteConfirm={() => {
            setShowDeleteDialog(false);
            if (selectedMemberWork) {
              NetworkCalls.deleteMemberWorkExtended(
                selectedMemberWork.id!
              ).then((result) => {
                setMemberWorkData(result);
                setSelectedMemberWork(undefined);
              });
            }
          }}
          title="Löschen"
          message="Datensatz wirklich löschen?"
          show={showDeleteDialog}
          onHandleClose={() => setShowDeleteDialog(false)}
        />

        <GenericDialog
          onConfirm={() => {
            setShowSendPushNotificationDialog(false);
            if (selectedMemberWork) {
              NetworkCalls.resendPushNotificationMemberWork(
                selectedMemberWork.id!
              ).then(() => {
              });
            }
          }}
          title="Pushnachricht senden?"
          message="Alle arbeitsdienstpflichtigen Mitglieder über den Arbeitsdienst via Pushnachricht informieren?"
          show={showSendPushNotificationDialog}
          onHandleClose={() => setShowSendPushNotificationDialog(false)}
        />

        {selectedMemberWork ? showDetails() : <EmptyPage />}
        <SearchMember
          onClose={() => setShowMemberSearch(false)}
          onSelectedMembers={async (members) => {
            if (members.length > 0 && selectedMemberWork) {
              for (const member of members) {
                await NetworkCalls.applyForMemberWork({
                  memberId: member.number,
                  lastName: member.lastname,
                  memberWorkId: selectedMemberWork.id!
                });
              }
              loadMemberWorkAttendees();
            }
          }}
          show={showMemberSearch}
          excludeNumbers={excludedNumbers}
        />
      </div>
    );
  };

  const showContentNew = () => {
    return (
      <div>
        <div className="row" style={{height: '100vh'}}>
          <div
            className="col-4"
            style={{
              position: 'sticky',
              top: 0,
              height: '100vh',
              maxHeight: '100vh',
              overflowY: 'auto',
              paddingRight: '0.75rem'
            }}
          >
            <div className="card shadow-sm border-0">
              <div className="card-header d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-3">
                  <h3 className="card-title m-0">Arbeitsdienste</h3>
                  <label className="form-check form-switch m-0">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      onChange={async (e) => {
                        setShowByGone(e.target.checked);
                        setSelectedMemberWork(undefined);
                        const data = await NetworkCalls.getMemberWorkExtended(e.target.checked);
                        setMemberWorkData(data);
                      }}
                      checked={showByGone}
                    />
                    <span className="form-check-label">Zurückliegende</span>
                  </label>
                </div>

                <div className="d-flex align-items-center gap-2">
                  <button className="btn btn-primary btn-sm" onClick={handleShow}>
                    <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-plus me-1"
                         width="18" height="18" viewBox="0 0 24 24" strokeWidth="2"
                         stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                      <path stroke="none" d="M0 0h24v24H0z" />
                      <path d="M12 5l0 14" />
                      <path d="M5 12l14 0" />
                    </svg>
                    Neuer Eintrag
                  </button>
                </div>
              </div>

              <div className="list-group list-group-flush">
                {memberWorkData.map((mw) => (
                  <button
                    key={mw.id}
                    type="button"
                    className={
                      `list-group-item list-group-item-action py-3 ` +
                      (isActive(mw, selectedMemberWork)
                        ? 'active bg-primary text-white'
                        : '')
                    }
                    onClick={() => {
                      setSelectedMemberWork(mw);
                      setLat(mw.latitude);
                      setLng(mw.longitude);
                      setTabIndex(0);
                    }}
                  >
                    <div className="d-flex w-100 justify-content-between align-items-start">
                      <div className="me-2">
                        <div
                          className={`fw-semibold text-truncate ${isActive(mw, selectedMemberWork) ? 'text-white' : 'text-body'}`}>
                          {mw.title}
                        </div>

                        <small className={isActive(mw, selectedMemberWork) ? 'text-white-50' : 'text-muted'}>
                          {formatUTC(mw.start)}
                        </small>

                        {mw.additionalInformation && (
                          <div
                            className={`small mt-1 ${isActive(mw, selectedMemberWork) ? 'text-white-50' : 'text-muted'}`}>
                            {mw.additionalInformation}
                          </div>
                        )}
                      </div>
                    </div>

                    <div
                      className={`mt-2 d-flex align-items-center gap-2 ${isActive(mw, selectedMemberWork) ? 'text-white-50' : 'text-muted'}`}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                           viewBox="0 0 24 24" fill="none" stroke="currentColor"
                           strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 1 1 18 0z" />
                        <circle cx="12" cy="10" r="3" />
                      </svg>
                      <span>lat {mw.latitude.toFixed(4)}, lng {mw.longitude.toFixed(4)}</span>
                    </div>
                  </button>
                ))}

                {memberWorkData.length === 0 && (
                  <div className="list-group-item text-muted py-4 text-center">
                    Keine Arbeitsdienste gefunden
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-md-8"
               style={{
                 height: '100vh',
                 maxHeight: '100vh',
                 overflowY: 'auto',
                 paddingLeft: '0.75rem'
               }}>
            <div className="p-3">
              {selectedMemberWork ? showAllDetails() : <EmptyPage />}
            </div>
          </div>
        </div>
      </div>
    );
  };

  function validateDateInput(currentDate?: Moment, selectedDate?: Moment) {
    if (!currentDate) return true;
    let now = new Date();
    return currentDate.isSameOrAfter(now) && currentDate.isBefore(maxDate);
  }

  function onDateTimeChange(value: Moment | string) {
    if (isMoment(value)) {
      let x = value.minute() % 15;
      if (x !== 0) {
        if (x > 7) {
          value.minute(value.minute() + 15 - x);
        } else {
          value.minute(value.minute() - x);
        }
      }

      let date = value.toDate();

      setAppointment(date);
      // setTime(date);
    } else {
      console.error('onDateTimeChange error', value);
    }
  }

  const detailsPageMemberWork = (mapId: string) => {
    return (
      <Form>
        <Form.Group>
          <Form.Label className="required">Titel des Arbeitsdienst</Form.Label>
          <Form.Control
            id="title"
            type="text"
            value={title ?? ''}
            disabled={showByGone && !show}
            placeholder="Der Name des Arbeitsdiensts"
            onChange={(e) => setTitle(e.target.value)}
          />
        </Form.Group>
        <Form.Group>
          <Form.Label className="required">
            Anzahl Teilnehmer maximal
          </Form.Label>
          <Form.Control
            type="number"
            disabled={showByGone && !show}
            placeholder="Anzahl Teilnehmer maximal"
            value={maxParticipants ?? ''}
            maxLength={2}
            max={99}
            onChange={(e) => setMaxParticipants(Number(e.target.value))}
          />
        </Form.Group>
        <Form.Group>
          <Form.Label className="required">
            Datum, wann der Arbeitsdienst stattfinden soll
          </Form.Label>
          <Col>
            <DateTime
              timeConstraints={timeConstraints}
              isValidDate={validateDateInput}
              locale="de"
              dateFormat="DD.MM.yyyy"
              timeFormat="HH:mm"
              utc
              value={appointment ?? undefined}
              onChange={onDateTimeChange}
              inputProps={{placeholder: 'TT.MM.JJJJ, HH:mm'}}
            />
          </Col>
        </Form.Group>
        <Form.Group>
          <Form.Label className="required">Treffpunkt</Form.Label>
          <Col>
            <Map
              key={selectedMemberWork?.id ?? 'no-selection'} // hartes Remount beim Wechsel
              style={divStyle}
              defaultCenter={{lat: Number(lat ?? 52), lng: Number(lng ?? 9)}}
              defaultZoom={8}
              gestureHandling="greedy"
              disableDefaultUI={true}
              mapId={mapId}
            >
              <Marker
                key={`${lat}-${lng}`} // Marker sicher neu rendern
                position={{lat: Number(lat ?? 52), lng: Number(lng ?? 9)}}
                clickable
                draggable
                onDragEnd={(evt) => {
                  if (evt?.latLng) {
                    setLat(evt.latLng.lat());
                    setLng(evt.latLng.lng());
                  }
                }}
                title="Treffpunkt"
              />
            </Map>
          </Col>
        </Form.Group>
        <Form.Group>
          <Form.Label>Zusätzliche Informationen (Optional)</Form.Label>
          <Col>
            <Form.Control
              disabled={showByGone && !show}
              as="textarea"
              rows={3}
              value={additionalInformation ?? ''}
              onChange={handleChange}
            />
          </Col>
        </Form.Group>
      </Form>
    );
  };

  const showDetails = () => {
      if (!selectedOrganisation) {
          return <></>;
      }
    return (
      <div className="card">
        <div className="card-header">
          <ul className="nav nav-tabs card-header-tabs">
            <li className="nav-item">
              <Link
                to="#"
                className={tabIndex === 0 ? activeClasses : inactiveClasses}
                onClick={() => setTabIndex(0)}
              >
                Bearbeiten
              </Link>
            </li>
            <li className="nav-item">
              <Link
                to="#"
                className={tabIndex === 1 ? activeClasses : inactiveClasses}
                onClick={() => setTabIndex(1)}
              >
                Teilnehmer
              </Link>
            </li>
          </ul>
        </div>
        <DeleteDialog
          onDeleteConfirm={() => {
            setShowDeleteAttendeeDialog(false);
            if (selectedMemberWork && selectedAttendee) {
              NetworkCalls.removeMemberWorkAttendee(
                selectedMemberWork,
                selectedAttendee
              ).then((result) => {
                setMemberWorkAttendees(result);
              });
            }
          }}
          title={`${selectedAttendee?.firstName} ${selectedAttendee?.lastName} entfernen`}
          message={`${selectedAttendee?.firstName} ${selectedAttendee?.lastName} wirklich vom Arbeitsdienst entfernen?`}
          show={showDeleteAttendeeDialog}
          onHandleClose={() => setShowDeleteAttendeeDialog(false)}
        />
        <div className="card-body">
          {tabIndex === 0
            ? detailsPageMemberWork('detailMap')
            : showParticipants()}
        </div>
        {tabIndex === 1 || showByGone ? (
          <div className="card-footer">
            <div className="d-flex">
              <Button
                variant="primary"
                onClick={() => {
                  loadMemberWorkAttendees();
                  setShowMemberSearch(true);
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="icon icon-tabler icon-tabler-plus"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  stroke="currentColor"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                  <path d="M12 5l0 14"></path>
                  <path d="M5 12l14 0"></path>
                </svg>
                Teilnehmer hinzufügen
              </Button>
            </div>
          </div>
        ) : (
          <div className="card-footer">
            <div className="d-flex">
              <Button
                onClick={() => {
                  if (selectedMemberWork && appointment) {
                    update({
                      id: selectedMemberWork.id,
                      title: title!,
                      maxParticipants: maxParticipants!,
                      start: getAppointmentWithSelectedTime(appointment),
                      latitude: lat!,
                      longitude: lng!,
                      additionalInformation,
                      organisationId: selectedOrganisation.id
                    }).then(() => setShow(false));
                  }
                }}
                variant="primary"
              >
                Speichern
              </Button>
              <Button
                className="btn btn-warning ms-auto"
                onClick={() => setShowSendPushNotificationDialog(true)}
              >
                Pushnachricht senden
              </Button>
              <Button
                className="btn btn-danger ms-auto"
                onClick={() => setShowDeleteDialog(true)}
              >
                Löschen
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const getAppointmentWithSelectedTime = (dt: Date) => {
    const copy = new Date(dt);
    copy.setSeconds(0, 0);
    return copy;
  };

  return (
    <div>
      <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_KEY!}>
        {showContentNew()}
        {ModalDialog()}
      </APIProvider>
    </div>
  );
}
